/**
 * Represents a navigation link in the navbar.
 *
 * @interface NavLink
 * @property {string} text - The display text for the link
 * @property {string} path - The route path for the link
 * @property {boolean} visible - Whether the link should be displayed in the navbar
 * @property {boolean} enabled - Whether the link is clickable (disabled links are shown but not interactive)
 */
export interface NavLink {
  text: string;
  path: string;
  url: boolean;
  visible: boolean;
  enabled: boolean;
  newTag?: boolean;
}

/**
 * Represents a button in the navbar.
 *
 * @interface NavButton
 * @property {string} text - The display text for the button
 * @property {string} path - The route path the button navigates to
 */
export interface NavButton {
  text: string;
  path: string;
}

/**
 * Configuration settings for navbar behavior.
 *
 * @interface NavbarSettings
 * @property {boolean} autoHide - Whether the navbar should auto-hide on scroll
 * @property {number} autoShowTimeout - Milliseconds until navbar reappears after hiding
 */
export interface NavbarSettings {
  autoHide: boolean;
  autoShowTimeout: number;
}

/**
 * Comprehensive navbar content and configuration structure.
 * Contains all data needed to render and control the navbar.
 *
 * @interface NavbarContent
 * @property {NavLink[]} links - Array of navigation links to display
 * @property {Object} buttons - Authentication buttons configuration
 * @property {NavButton} buttons.signUp - Sign up button configuration
 * @property {NavButton} buttons.signIn - Sign in button configuration
 * @property {NavbarSettings} settings - Behavior settings for the navbar
 */
export interface NavbarContent {
  links: NavLink[];

  settings: NavbarSettings;
}

/**
 * Default navbar content configuration.
 * Centralizes all navbar-related content and settings in one location
 * for easier maintenance and configuration.
 *
 * @constant navbarContent
 * @type {NavbarContent}
 *
 * @example
 * // Accessing navbar links
 * navbarContent.links.forEach(link => {
 *   if (link.visible && link.enabled) {
 *     console.log(`Active link: ${link.text} -> ${link.path}`);
 *   }
 * });
 *
 * @example
 * // Using navbar settings in a component
 * const { autoHide, autoShowTimeout } = navbarContent.settings;
 * const navbarHook = useNavbar({ autoHide, autoShowTimeout });
 */
export const navbarContent: NavbarContent = {
  links: [
    {
      text: "Home",
      path: "/",
      url: true,
      visible: true,
      enabled: true,
    },
    {
      text: "FAQs",
      path: "#faqSection",
      url: false,
      visible: true,
      enabled: true,
    },
    {
      text: "Resources",
      path: "/resources",
      visible: false,
      enabled: false,
      url: true,
    },
    {
      text: "About",
      path: "/about",
      url: true,
      visible: true,
      enabled: true,
    },
    {
      text: "Contact",
      path: "#contact",
      url: false,
      visible: false,
      enabled: false,
    },
  ],

  settings: {
    autoHide: true,
    autoShowTimeout: 3000,
  },
};
